/**
 * Uploaded Documents (T7.24, T7.25; design.md § 10a, 7.3).
 *
 * The tab that was Architecture. It is now on every blueprint rather than only
 * the seeded one, and it shows what people actually uploaded instead of a fixed
 * diagram.
 *
 * Two sections, because two different things were asked for:
 *
 *   Pictures  rendered inline, each under its heading. Up to 2 MB.
 *   Files     a table of attachments, with a download link. Up to 10 MB.
 *
 * **A picture is referenced, never inlined.** The API serves it with
 * `Content-Security-Policy: sandbox` and `X-Content-Type-Options: nosniff`,
 * which is what makes an uploaded SVG safe to show. Those headers travel with
 * the response, so the bytes have to be fetched by the browser as their own
 * resource — an `<img src>` pointing at the content endpoint. Putting the markup
 * inline, as an `<svg>` element or a data: URI, would run it on our own origin
 * with the headers thrown away. Do not change these to inline anything.
 *
 * **Viewers.** `canEdit` is false for a viewer, so `DocumentControls` and the
 * two upload buttons are not rendered at all. Absent, not disabled: there is no
 * control in the DOM to enable, and the API refuses the write regardless.
 *
 * "Uploaded Documents", "Pictures" and "Files" are not among the nine
 * client-overridable display terms (design.md § 2.1), so they are fixed strings.
 * The word for the blueprint itself is not, and comes from `useTerms()`.
 */
import { useTerms } from '../../lib/terms';
import {
  contentUrl,
  formatSize,
  formatUploadedAt,
  useDocuments,
  type BlueprintDocument,
} from '../../lib/documents';
import { DocumentControls } from './DocumentControls';
import { UploadDocumentDialog } from './UploadDocumentDialog';
import '../../styles/documents.css';

function Picture({
  blueprintId,
  document: doc,
  siblings,
  canEdit,
}: {
  blueprintId: string;
  document: BlueprintDocument;
  siblings: BlueprintDocument[];
  canEdit: boolean;
}) {
  return (
    <figure className="doc-picture">
      <figcaption className="doc-picture-head">
        <h3 title={doc.heading}>{doc.heading}</h3>
        {canEdit ? (
          <DocumentControls blueprintId={blueprintId} document={doc} siblings={siblings} />
        ) : null}
      </figcaption>
      {/* Referenced, not inlined — see the note at the top of this file. */}
      <img src={contentUrl(blueprintId, doc.id)} alt={doc.heading} loading="lazy" />
      <p className="doc-picture-meta">
        {/* The filename carries its own class because selection is enabled for
            filenames and headings only, and here it shares a line with the size
            and the uploader, which are not either. */}
        <span className="doc-name">{doc.filename}</span> · {formatSize(doc.sizeBytes)} · uploaded by{' '}
        {doc.uploadedByName ?? 'a removed account'} on {formatUploadedAt(doc.uploadedAt)}
      </p>
    </figure>
  );
}

function FileRow({
  blueprintId,
  document: doc,
  siblings,
  canEdit,
}: {
  blueprintId: string;
  document: BlueprintDocument;
  siblings: BlueprintDocument[];
  canEdit: boolean;
}) {
  return (
    <tr>
      {/* Never truncated: the full heading and the full filename are both here,
          and the title carries them for a column narrow enough to ellipsise. */}
      <th scope="row" title={doc.heading}>
        {doc.heading}
      </th>
      <td className="doc-filename" title={doc.filename}>
        {doc.filename}
      </td>
      <td className="doc-size">{formatSize(doc.sizeBytes)}</td>
      <td>{doc.uploadedByName ?? 'a removed account'}</td>
      <td className="doc-when">{formatUploadedAt(doc.uploadedAt)}</td>
      <td className="doc-dl-col">
        {/* A plain link, so the API's Content-Disposition decides the filename and
            the usual browser affordances (open in a new tab, copy the address)
            all work. The response is `attachment` whatever the file claims to be. */}
        <a
          className="doc-download"
          href={contentUrl(blueprintId, doc.id)}
          download={doc.filename}
        >
          Download
        </a>
      </td>
      {canEdit ? (
        <td className="doc-row-actions">
          <DocumentControls blueprintId={blueprintId} document={doc} siblings={siblings} />
        </td>
      ) : null}
    </tr>
  );
}

export function DocumentsView({
  blueprintId,
  canEdit,
}: {
  blueprintId: string;
  /** design.md § 5: editors and admins manage documents; viewers see and download. */
  canEdit: boolean;
}) {
  const terms = useTerms();
  const query = useDocuments(blueprintId);

  const documents = query.data ?? [];
  const pictures = documents.filter((d) => d.kind === 'picture');
  const files = documents.filter((d) => d.kind === 'file');

  if (query.isPending) {
    return (
      <div className="empty" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="empty" role="alert">
        Could not load the documents for this {terms.noun('blueprint')}.
        <br />
        {query.error instanceof Error ? query.error.message : 'Try again in a moment.'}
      </div>
    );
  }

  return (
    <div id="view-documents" className="doc-view">
      <div className="tl-panel">
        <div className="tl-eyebrow">
          <span className="dash" />
          {terms.noun('blueprint')} &middot; Reference material
        </div>
        <h2>Uploaded Documents</h2>
        <p className="tl-sub">
          Diagrams, screenshots and attachments kept alongside this{' '}
          {terms.noun('blueprint')}. They belong to the {terms.noun('blueprint')} itself, not to one
          version, so publishing a new version leaves them as they are.
        </p>
      </div>

      <section className="doc-section" aria-labelledby="doc-pictures-head">
        <div className="doc-section-head">
          <h2 id="doc-pictures-head">Pictures</h2>
          {canEdit ? (
            <UploadDocumentDialog blueprintId={blueprintId} kind="picture" label="Add a picture" />
          ) : null}
        </div>

        {pictures.length === 0 ? (
          <p className="doc-empty">
            No pictures yet. This is where an architecture diagram, a screenshot or a photograph of
            a whiteboard goes, shown full width under its own heading.
            {canEdit ? ' Each one is up to 2 MB.' : ''}
          </p>
        ) : (
          <div className="doc-pictures">
            {pictures.map((d) => (
              <Picture
                key={d.id}
                blueprintId={blueprintId}
                document={d}
                siblings={pictures}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}
      </section>

      <section className="doc-section" aria-labelledby="doc-files-head">
        <div className="doc-section-head">
          <h2 id="doc-files-head">Files</h2>
          {canEdit ? (
            <UploadDocumentDialog blueprintId={blueprintId} kind="file" label="Add a file" />
          ) : null}
        </div>

        {files.length === 0 ? (
          <p className="doc-empty">
            No files yet. This is where the specifications, notes and spreadsheets that explain this{' '}
            {terms.noun('blueprint')} go, listed here for anyone who can see it to download.
            {canEdit ? ' Each one is up to 10 MB.' : ''}
          </p>
        ) : (
          <div className="doc-scroll">
            <table className="doc-table">
              <thead>
                <tr>
                  <th scope="col">Heading</th>
                  <th scope="col">File</th>
                  <th scope="col">Size</th>
                  <th scope="col">Uploaded by</th>
                  <th scope="col">Uploaded</th>
                  <th scope="col" className="doc-dl-col">
                    <span className="sr-only">Download</span>
                  </th>
                  {canEdit ? (
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {files.map((d) => (
                  <FileRow
                    key={d.id}
                    blueprintId={blueprintId}
                    document={d}
                    siblings={files}
                    canEdit={canEdit}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
